import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { cacheHost } from 'redux/slices/host-slice';

let localHostState: string | undefined;

// Shopify only puts `host` on the initial iframe URL, and both the redux cache and
// localHostState die on a full page reload (Vite dev reloads, manual refresh). sessionStorage
// keeps it per-tab, so two admin tabs for different shops never share a host.
const HOST_STORAGE_KEY = 'app_bridge_host';

const readStoredHost = (): string | undefined => {
    try {
        return sessionStorage.getItem(HOST_STORAGE_KEY) || undefined;
    } catch {
        return undefined;
    }
};

const storeHost = (host: string) => {
    try {
        sessionStorage.setItem(HOST_STORAGE_KEY, host);
    } catch {
        // storage unavailable (private mode / blocked cookies) - the in-memory caches still apply
    }
};

const useAppBridgeConfig = () => {
    let host = new URLSearchParams(location.search).get('host');

    const hostState = useAppSelector((state) => state.hostSlice);

    if (hostState && hostState.host) {
        host = hostState.host;
    }

    if (!host && !hostState?.host) {
        host = localHostState ?? readStoredHost();
    }

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!host) return;
        localHostState = host;
        storeHost(host);
        if (!hostState?.host) {
            dispatch(cacheHost(host));
        }
    }, [host]);

    const config = {
        apiKey: import.meta.env.VITE_APP_CLIENT_ID,
        host: host ?? '',
        forceRedirect: true,
    };

    return { config };
};

export default useAppBridgeConfig;
